import { z } from 'zod';

/**
 * Which backend the screen is pointed at.
 *
 * ## A scope, not a filter
 *
 * The flags screens have an environment axis too, and copying it here would be
 * wrong. A flag is one row in one shared table partitioned by environment, so
 * its four values belong to the same object and `EnvironmentMatrix` compares
 * them side by side. A client is not: it lives in `veev-{env}-tenants-catalog`
 * with its own Cognito pools and its own identity-api, so the four are four
 * separate stores and a client in dev does not exist in prd at all. Nothing to
 * compare — so this decides which backend the whole screen talks to.
 *
 * ## `eph` needs a slot, and cannot be represented without one
 *
 * Upstream inserts the slot as its own segment of every resource name when the
 * environment is `eph`, so two slots share nothing but a prefix. Without one
 * there is no deployment to address, which is why {@link formatTarget} refuses
 * to produce a string for it rather than emitting something that would key a
 * cache and address nothing.
 */

export const CLIENT_ENVIRONMENTS = ['dev', 'stg', 'prd', 'eph'] as const;
export const ClientEnvironment = z.enum(CLIENT_ENVIRONMENTS);
export type ClientEnvironment = z.infer<typeof ClientEnvironment>;

export const ENVIRONMENT_LABEL: Record<ClientEnvironment, string> = {
    dev: 'Dev',
    stg: 'Staging',
    prd: 'Production',
    eph: 'Ephemeral slot',
};

/**
 * One line on what each target covers, shown next to the switcher.
 *
 * `prd` says what it is rather than what it is called, because that is the one
 * an operator needs to notice without reading it.
 */
export const ENVIRONMENT_BLURB: Record<ClientEnvironment, string> = {
    dev: 'The dev account.',
    stg: 'The staging account.',
    prd: 'Real clients and real customer data.',
    eph: 'A review stage — each slot is its own deployment.',
};

export interface ClientTarget {
    readonly env: ClientEnvironment;
    /** Present only for `eph`, where it is required. */
    readonly slot?: string;
}

/** Mirrors upstream's `sanitizeStackSuffix`, so one deployment has one id. */
export function normaliseSlot(slot: string): string {
    return slot
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
}

/** `null` when an ephemeral target names no slot — there is nothing to address. */
export function formatTarget(target: ClientTarget): string | null {
    if (target.env !== 'eph') return target.env;

    const slot = normaliseSlot(target.slot ?? '');
    return slot.length === 0 ? null : `eph:${slot}`;
}

/** What the operator sees: `Production`, or `Ephemeral slot · dev1`. */
export function describeTarget(target: ClientTarget): string {
    if (target.env !== 'eph') return ENVIRONMENT_LABEL[target.env];

    const slot = normaliseSlot(target.slot ?? '');
    return slot.length === 0 ? 'Ephemeral slot · none' : `Ephemeral slot · ${slot}`;
}

/** The query string every request carries. */
export function targetParams(target: ClientTarget): Record<string, string> {
    return target.env === 'eph' && target.slot
        ? { env: target.env, slot: normaliseSlot(target.slot) }
        : { env: target.env };
}
